import React, { useState, useEffect, useCallback } from 'react';
import { Input } from "./ui/input";
import { XCircle } from 'lucide-react';
import YearPicker from "./ui/YearPicker";
import "react-datepicker/dist/react-datepicker.css";
import { useApiCaller } from '../utils/ApiUtilities';
import { getAdminQuestionUrl, getTagUrl, getAdminQuizPutUrl, getAdminQuestionGetByIdUrl } from '../utils/UrlUtilities';
import { constructQuizPostPayload } from '../utils/PayloadUtils';
import { QUESTIONDIFFICULTY, TAGLEVELS, QUIZTYPE, DIVISION_VALUES, UNIT_VALUES, isDivisionLevel, isUnitLevel } from '../utils/Constants';
import { useUser } from '../contexts/UserContext'; // Assuming you have a UserContext
import "./components.css";
import { extractQuestionIdsBySubject, getTotalQuestions } from '../utils/BasicUtilities';
import { QuestionFilterBar, QuestionSelectionGrid, SubjectQuestionGrid } from './QuizGrid';
import { isModelTestLevel, blueprintFor } from '../utils/modelTestBlueprint';
import QuestionEditForm from './QuestionEditForm';

const formatDateForDateTimeLocal = (dateString) => {
    if (!dateString) return '';
    try {
        const d = new Date(dateString);
        const year = d.getFullYear();
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const day = d.getDate().toString().padStart(2, '0');
        const hours = d.getHours().toString().padStart(2, '0');
        const minutes = d.getMinutes().toString().padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (e) {
        console.error("Error formatting date for datetime-local:", dateString, e);
        return '';
    }
};

function QuizEditDetailsForm({
    quizId,
    title, onTitleChange,
    description, onDescriptionChange,
    startTime, onStartTimeChange,
    durationMinutes, onDurationMinutesChange, durationAuto,
    openHours, onOpenHoursChange,
    level, onLevelChange,
    division, onDivisionChange, unit, onUnitChange,
    createdBy, onCreatedByChange,
    type, onTypeChange,
    year, onYearChange,
    questionsForQuiz, onRemoveQuestion, onEditQuestion,
    quizSubjects, onQuizSubjectSelect, onRemoveQuizSubject,
    tags,
    moduleCount, onModuleCountChange, durations, onDurationChange,
    isSaving, error, isSaveDisabled, onSaveClick, onCancelClick, isModelTest
}) {
    // TOEFL and IELTS share the model-test builder UI; the blueprint helpers are picked by level.
    // `isModelTest` is passed in (based on the quiz's real blueprint), not inferred from level.
    const bp = blueprintFor(level);

    return (
        <div className="card shadow-sm p-4 h-full" style={{ borderTop: '4px solid #7c3aed' }}>
            <h2 className="fw-bold mb-3" style={{ fontSize: '1.15rem', color: '#5b21b6' }}>Edit Test Details</h2>
            {error && <div className="alert alert-danger">{error}</div>}
            <div className="space-y-4">
                <div className="my-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Test Title <span className="text-danger">*</span>
                    </label>
                    <textarea
                        value={title}
                        onChange={onTitleChange}
                        className="form-control"
                        rows={1}
                        placeholder="Enter test title"
                        required
                    />
                </div>
                <div className="my-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Test Description
                    </label>
                    <textarea
                        value={description}
                        onChange={onDescriptionChange}
                        className="form-control"
                        rows={2}
                        placeholder="Enter test description"
                    />
                </div>
                <div className="my-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Test Level <span className="text-danger">*</span></label>
                    <select
                        value={level}
                        onChange={onLevelChange}
                        className="form-select"
                        required
                    >
                        <option value="" disabled>SELECT</option>
                        {TAGLEVELS.map((item) => (
                            item.submenu ? (
                                <optgroup key={item.value} label={item.label}>
                                    {item.submenu.map((sub) => (
                                        <option key={sub.value} value={sub.value}>
                                            {sub.label}
                                        </option>
                                    ))}
                                </optgroup>
                            ) : (
                                <option key={item.value} value={item.value}>
                                    {item.label}
                                </option>
                            )
                        ))}
                    </select>
                </div>
                {isDivisionLevel(level) && (
                    <div className="my-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Division</label>
                        <select value={division || ''} onChange={onDivisionChange} className="form-select">
                            <option value="">All / Mixed</option>
                            {DIVISION_VALUES.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                    </div>
                )}
                {isUnitLevel(level) && (
                    <div className="my-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                        <select value={unit || ''} onChange={onUnitChange} className="form-select">
                            <option value="">All / Mixed</option>
                            {UNIT_VALUES.map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                    </div>
                )}

                <div className="my-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Test Type <span className="text-danger">*</span></label>
                    <select
                        value={type}
                        onChange={onTypeChange}
                        className="form-select"
                        required
                    >
                        <option value="" disabled>SELECT</option>
                        {QUIZTYPE.map((qType) => (
                            <option key={qType} value={qType}>
                                {qType}
                            </option>
                        ))}
                    </select>
                </div>
                {type === 'previous_year' &&
                    <div className="my-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Exam Year <span className="text-danger">*</span>
                        </label>
                        <YearPicker
                            value={year || ''}
                            onChange={y => onYearChange({ target: { value: y } })}
                            placeholder="SELECT"
                        />
                    </div>
                }
                {type && type === 'compitition' &&
                    <div className="my-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Open for (hours after publish) <span className="text-danger">*</span>
                        </label>
                        <Input
                            type="number"
                            min="1"
                            value={openHours}
                            onChange={onOpenHoursChange}
                            className="form-control"
                            placeholder="e.g. 24"
                            required
                        />
                        <small className="form-text text-muted">
                            Users may start within this many hours after the test is published. The window
                            is recomputed from the publish time; each attempt is timed per section.
                        </small>
                    </div>
                }
                {!isModelTest && (
                <div className="my-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Duration In Minutes {durationAuto ? <span className="text-muted small">(optional — leave blank to auto-allocate)</span> : <span className="text-danger">*</span>}</label>
                    <Input
                        type="number"
                        placeholder={durationAuto ? 'Auto-allocated if left blank' : 'Duration'}
                        value={durationMinutes}
                        onChange={onDurationMinutesChange}
                        className="w-full"
                        required={!durationAuto}
                    />
                </div>
                )}
                
                <div className="my-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Created By <span className="text-danger">*</span></label>
                    <Input
                        type="email"
                        placeholder="Creator Email"
                        value={createdBy}
                        onChange={onCreatedByChange}
                        className="w-full"
                        required
                    />
                </div>

                {level && level.length > 0 && isModelTest && (
                    <div className="w-full">
                        <label className="block text-sm font-medium text-gray-700 mb-1">{level} Sections &amp; Modules</label>
                        <small className="form-text text-muted d-block mb-2">Set modules per section and each module's time. Assign questions to a section-module from the Available Questions grid.</small>
                        {bp.SECTIONS.map(sec => {
                            const count = sec.maxModules === 1 ? 1 : (moduleCount[sec.key] || 1);
                            return (
                                <div key={sec.key} className="border rounded p-2 mb-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <strong>{sec.name}</strong>
                                        {sec.maxModules > 1 && (
                                            <select className="form-select form-select-sm w-auto" value={moduleCount[sec.key] || 1}
                                                onChange={e => onModuleCountChange(sec, Number(e.target.value))}>
                                                {Array.from({ length: sec.maxModules }).map((_, n) => (
                                                    <option key={n + 1} value={n + 1}>{n + 1} module{n > 0 ? 's' : ''}</option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                    <div className="d-flex flex-wrap gap-3 mt-2">
                                        {Array.from({ length: count }).map((_, i) => {
                                            const bucket = bp.bucketKey(sec.name, i);
                                            return (
                                                <div key={i} className="d-flex align-items-center gap-1">
                                                    <span className="small text-muted">{count > 1 ? `Module ${i + 1}` : 'Time'}:</span>
                                                    <input type="number" min="1" style={{ width: 70 }} className="form-control form-control-sm"
                                                        value={durations[bucket] ?? sec.defaultMinutes}
                                                        onChange={e => onDurationChange(d => ({ ...d, [bucket]: e.target.value }))} />
                                                    <span className="small text-muted">min · {(questionsForQuiz[bucket] || []).length} Q</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                {level && level.length > 0 && !isModelTest &&
                    <div className="w-full">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Test Subjects <span className="text-danger">*</span></label>
                        <div className="d-flex flex-wrap gap-2 mb-2">
                            {quizSubjects.map((sub) => (
                                <span
                                    key={sub}
                                    className="badge bg-primary-subtle text-primary d-flex align-items-center rounded-pill px-2 py-1"
                                >
                                    {sub}
                                    <XCircle className="text-danger cursor-pointer ms-1" size={16} onClick={() => onRemoveQuizSubject(sub)}/>
                                </span>
                            ))}
                        </div>
                        <select
                            value=""
                            onChange={onQuizSubjectSelect}
                            className="form-select"
                            disabled={!level}
                        >
                            <option value="" disabled>Add Subject to Test</option>
                            {tags
                                .filter((tag) => tag.tag_type === 'SUBJECT' && tag.level === level)
                                .filter((tag) => !quizSubjects.includes(tag.name))
                                .map((tag) => (
                                    <option key={tag.name} value={tag.name}>
                                        {tag.name}
                                    </option>
                                ))}
                        </select>
                        {!level && <small className="form-text text-muted">Select a Level to add Subjects.</small>}
                    </div>
                }
                <div className="text-end my-3">
                    <p className="fw-bold">Total Questions Added: {getTotalQuestions(questionsForQuiz)}</p>
                </div>

                {quizSubjects.map(subjectName => (
                    <SubjectQuestionGrid
                        key={subjectName}
                        subjectName={subjectName}
                        assignedQuestions={questionsForQuiz[subjectName] || []}
                        onRemoveQuestion={onRemoveQuestion}
                        onEditQuestion={onEditQuestion}
                    />
                ))}

                <div className="d-flex justify-content-end gap-2 mt-4">
                    <button className='btn btn-danger' onClick={onCancelClick} disabled={isSaving}>
                        Cancel
                    </button>
                    <button className='btn btn-primary' onClick={onSaveClick} disabled={isSaveDisabled}>
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// --- QuizEditManagementPage Component (NEW Structure) ---
export default function QuizEditManagementPage({ quiz, onCancel, onSave }) {
    const [currentPage, setCurrentPage] = useState(1);
    // States for quiz metadata
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [startTime, setStartTime] = useState('');
    const [durationMinutes, setDurationMinutes] = useState(60);
    // Competition: how many hours the quiz is open for starting after publish (metadata.open_hours).
    const [openHours, setOpenHours] = useState('');
    const [level, setLevel] = useState('');
    const [division, setDivision] = useState('');
    const [unit, setUnit] = useState('');
    const [createdBy, setCreatedBy] = useState('');
    const [type, setType] = useState('');
    const [year, setYear] = useState('');

    // States for question filtering and selection
    const [questionFilters, setQuestionFilters] = useState({
        level: "", difficulty: "", search: "", topic: "", subject: "", exam: "", random: false,
    });
    const [questionsForQuiz, setQuestionsForQuiz] = useState({}); // { subjectName: [questionObject, ...], ... }
    const [selectedQuizSubjects, setSelectedQuizSubjects] = useState([]); // Subjects chosen for the quiz
    // TOEFL model test: module counts per section + per-bucket durations.
    const [moduleCount, setModuleCount] = useState({ reading: 2, listening: 2, writing: 1, speaking: 1, reading_writing: 2, math: 2 });
    const [durations, setDurations] = useState({});
    // Model-test mode is driven by the quiz's actual blueprint (metadata.test_kind), NOT just its
    // level — a competition quiz is TOEFL-level but flat (no test_kind), so it edits as plain
    // subject buckets (Reading/Listening) rather than the section/module builder.
    const isModelTest = isModelTestLevel(level) && !!(quiz && quiz.metadata && quiz.metadata.test_kind);
    const bp = blueprintFor(level);
    // BCS published/competition/micro/previous-year auto-allocate time when duration is left blank.
    const durationAuto = level === 'BCS' && ['published', 'compitition', 'micro', 'previous_year'].includes(type);

    const CallApi = useApiCaller();
    const [tags, setTags] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    // Click-to-edit: hold the FULL question (fetched by id, so answers/options aren't the
    // taker-safe stripped copy) for the QuestionEditForm modal.
    const [editingQuestion, setEditingQuestion] = useState(null);
    const setId = 1; // Assuming set_id is always 1 for this mock

    // Open the edit-question modal for a quiz/available question: fetch the full admin question
    // first (assigned questions from an existing quiz come back answer-stripped, so editing the
    // in-memory copy could blank the correct answer on save).
    const handleEditQuestion = useCallback(async (question) => {
        const id = question?.question_id;
        if (!id) return;
        const res = await CallApi({ url: getAdminQuestionGetByIdUrl(id), method: 'GET', token: true });
        if (res.success) setEditingQuestion(res.data);
    }, [CallApi]);

    // Fetch tags on mount
    useEffect(() => {
        const fetchTags = async () => {
            try {
                const tagUrl = getTagUrl();
                const tagGetResponse = await CallApi({
                    url: tagUrl,
                    method: 'GET',
                    token: true,
                });
                if (tagGetResponse.success) {
                    setTags(tagGetResponse.data.tags);
                } else {
                    setTags([]);
                }
            } catch (error) {
                console.error("Error fetching tags:", error);
                setTags([]);
            }
        };
        fetchTags();
    }, []);

    // Initialize/reset states when `quiz` prop changes
    useEffect(() => {
        if (quiz) {
            setTitle(quiz.title || '');
            setDescription(quiz.description || '');
            setStartTime(formatDateForDateTimeLocal(quiz.start_time));
            setDurationMinutes(quiz.duration_minutes || 60);
            setLevel(quiz.level || '');
            setDivision(quiz.division || '');
            setUnit(quiz.unit || '');
            setCreatedBy(quiz.created_by || '');
            setType(quiz.quiz_type || '');
            setYear(quiz.year != null ? String(quiz.year) : '');
            setOpenHours(quiz.metadata?.open_hours != null ? String(quiz.metadata.open_hours) : '');

            // Load questions/subjects. For a model test (TOEFL/IELTS), reconstruct the module
            // buckets, counts and durations from the saved blueprint; otherwise use subject buckets.
            const tk = quiz.metadata && quiz.metadata.test_kind;
            if (tk === 'toefl_model_test' || tk === 'ielts_model_test' || tk === 'sat_model_test') {
                const { questionsByBucket, moduleCount: mc, durations: dur } = blueprintFor(quiz.level).bucketsFromBlueprint(quiz.metadata, quiz.questions || {});
                setQuestionsForQuiz(questionsByBucket);
                setSelectedQuizSubjects(Object.keys(questionsByBucket));
                setModuleCount(prev => ({ ...prev, ...mc }));
                setDurations(dur);
            } else {
                setQuestionsForQuiz(quiz.questions || {});
                setSelectedQuizSubjects(Object.keys(quiz.question_ids || []));
            }

            // Set initial filter level to quiz level
            setQuestionFilters(prev => ({ ...prev, level: quiz.level || "" }));
        }
    }, [quiz]);

    // TOEFL: keep module buckets + duration defaults in sync with the section/module config.
    // Preserves already-assigned questions per bucket (from load or user edits).
    useEffect(() => {
        if (!isModelTest) return;
        const buckets = bp.bucketsFor(moduleCount);
        const keys = buckets.map(b => b.key);
        setSelectedQuizSubjects(keys);
        setQuestionsForQuiz(prev => {
            const next = {};
            keys.forEach(k => { next[k] = prev[k] || []; });
            return next;
        });
        setDurations(prev => {
            const d = { ...prev };
            buckets.forEach(b => {
                if (d[b.key] == null) d[b.key] = (bp.SECTIONS.find(s => s.key === b.sectionKey) || {}).defaultMinutes;
            });
            return d;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModelTest, level, moduleCount]);
    const handleFilterQuestions = useCallback((newFilters) => {
        setQuestionFilters(newFilters);
    }, []);

    const handleQuestionAssign = useCallback((question, subjectName) => {
        setQuestionsForQuiz(prev => {
            const currentQuestionsInSubject = prev[subjectName] || [];
            // Check if question already exists in this subject to prevent duplicates
            if (!currentQuestionsInSubject.some(q => q.question_id === question.question_id)) {
                return {
                    ...prev,
                    [subjectName]: [...currentQuestionsInSubject, question]
                };
            }
            return prev;
        });
        // window.confirm(`Question "${question.question_text.substring(0, Math.min(question.question_text.length, 30))}..." added to "${subjectName}"`);
    }, []);

    const handleRemoveQuestion = useCallback((questionId, subjectName) => {
        setQuestionsForQuiz(prev => {
            const newQuestions = { ...prev };
            if (newQuestions[subjectName]) {
                newQuestions[subjectName] = newQuestions[subjectName].filter(q => q.question_id !== questionId);
            }
            return newQuestions;
        });
    }, []);

    const handleQuizSubjectSelect = (e) => {
        const selectedSubject = e.target.value;
        if (selectedSubject && !selectedQuizSubjects.includes(selectedSubject)) {
            setSelectedQuizSubjects(prev => [...prev, selectedSubject]);
            // Also initialize an empty array for this new subject in questionsForQuiz
            setQuestionsForQuiz(prev => ({
                ...prev,
                [selectedSubject]: prev[selectedSubject] || []
            }));
        }
    };

    const handleRemoveQuizSubject = (subjectToRemove) => {
        setSelectedQuizSubjects(prev => prev.filter(sub => sub !== subjectToRemove));
        setQuestionsForQuiz(prev => {
            const newQuestions = { ...prev };
            delete newQuestions[subjectToRemove];
            return newQuestions;
        });
    };

    // Block reducing a section's module count while a to-be-removed module still holds questions.
    const guardedSetModuleCount = (sec, newCount) => {
        const cur = moduleCount[sec.key] || 1;
        if (newCount < cur) {
            for (let i = newCount; i < cur; i += 1) {
                const b = bp.bucketKey(sec.name, i);
                if ((questionsForQuiz[b] || []).length > 0) {
                    setError(`Remove all questions from ${sec.name} Module ${i + 1} before reducing to ${newCount} module.`);
                    return;
                }
            }
        }
        setError('');
        setModuleCount(c => ({ ...c, [sec.key]: newCount }));
    };

    const handleSave = async () => {
        setError('');
        if (!title || (type && type === 'compitition' && !(Number(openHours) > 0)) || (!durationAuto && !durationMinutes) || !level || !createdBy || !type || selectedQuizSubjects.length === 0 || getTotalQuestions(questionsForQuiz) === 0) {
            setError('Please fill all required fields and assign at least one question to a subject.');
            return;
        }
        if (type === 'previous_year' && !year) {
            setError('A previous-year test requires an Exam Year.');
            return;
        }
        setIsSaving(true);
        try {
            let putQuizPayload;
            if (isModelTest) {
                const { blueprint, questionIdsBySection, totalMinutes } = bp.buildBlueprint({ moduleCount, durations, questionsByBucket: questionsForQuiz, existingMetadata: quiz.metadata });
                if (Object.keys(questionIdsBySection).length === 0) {
                    setError('Assign at least one question to a module.');
                    setIsSaving(false);
                    return;
                }
                const isCompetition = type === 'compitition';
                // Micro (split) quizzes carry markers that place them under their section on the
                // listing (metadata.split_section -> Listening/Reading/…) and link them to their
                // source. buildBlueprint returns only { test_kind, sections }, so carry these back
                // over — otherwise a plain duration edit erases split_section and the micro falls
                // into "Other".
                const splitMarkers = {};
                ['split_from_quiz_id', 'split_section', 'split_module'].forEach((k) => {
                    if (quiz.metadata && quiz.metadata[k] !== undefined) splitMarkers[k] = quiz.metadata[k];
                });
                // Competitions: keep the competition markers + editable open_hours in metadata, and
                // don't send a manual window (the backend derives it from open_hours at/after publish).
                const metadata = isCompetition
                    ? {
                        ...blueprint,
                        competition: true,
                        open_hours: Number(openHours) || (quiz.metadata?.open_hours ?? 0),
                        source_reading_quiz_id: quiz.metadata?.source_reading_quiz_id,
                        source_listening_quiz_id: quiz.metadata?.source_listening_quiz_id,
                    }
                    : { ...blueprint, ...splitMarkers };
                const startArg = isCompetition ? '' : (startTime ? new Date(startTime).toISOString() : '');
                putQuizPayload = constructQuizPostPayload(
                    title, description, startArg, totalMinutes || durationMinutes,
                    level, createdBy, type, questionIdsBySection, setId, year, { division, unit, metadata }
                );
            } else {
                const questionIdsFromDetails = extractQuestionIdsBySubject(questionsForQuiz);
                putQuizPayload = constructQuizPostPayload(title, description, startTime ? new Date(startTime).toISOString() : "", durationMinutes, level, createdBy, type, questionIdsFromDetails, setId, year, { division, unit });
            }

            const putQuizUrl = getAdminQuizPutUrl(quiz.quiz_id, 1);
            const response = await CallApi({
                url: putQuizUrl,
                method: 'PUT',
                payload: putQuizPayload,
                token: true,
            });
            if (response.success) {
                onSave(response.data);
            } else {
                setError(response.message || 'Failed to update test.');
                console.error('Edit Quiz Error:', response);
            }
        } catch (e) {
            setError('An error occurred while updating the test.');
            console.error('Edit Quiz Error:', e);
        } finally {
            setIsSaving(false);
        }
    };

    const isSaveDisabled = isSaving || !title || (type && type === 'compitition' && !(Number(openHours) > 0)) || (type === 'previous_year' && !year) || (!durationAuto && !durationMinutes) || !level || !createdBy || !type || selectedQuizSubjects.length === 0 || getTotalQuestions(questionsForQuiz) === 0;

    return (
        <div className="container-fluid py-4">
            <h1 className="fw-bold mb-4" style={{ fontSize: '1.6rem', color: '#5b21b6' }}>Edit Test <span className="text-muted" style={{ fontWeight: 500, fontSize: '1.1rem' }}>({quiz?.title})</span></h1>

            <div className="row">
                {/* Left Column: Quiz Edit Form (Details & Assigned Questions) */}
                <div className="col-lg-6 mb-4">
                    <QuizEditDetailsForm
                        quizId={quiz?.quiz_id}
                        title={title} onTitleChange={e => setTitle(e.target.value)}
                        description={description} onDescriptionChange={e => setDescription(e.target.value)}
                        startTime={startTime} onStartTimeChange={e => setStartTime(e.target.value)}
                        durationMinutes={durationMinutes} onDurationMinutesChange={e => setDurationMinutes(e.target.value === '' ? '' : Number(e.target.value))} durationAuto={durationAuto}
                        openHours={openHours} onOpenHoursChange={e => setOpenHours(e.target.value)}
                        level={level} onLevelChange={e => {
                            setLevel(e.target.value);
                            setSelectedQuizSubjects([]); // Clear subjects if level changes
                            setQuestionsForQuiz({}); // Clear assigned questions
                            setDivision('');
                            setUnit('');
                        }}
                        division={division} onDivisionChange={e => setDivision(e.target.value)}
                        unit={unit} onUnitChange={e => setUnit(e.target.value)}
                        createdBy={createdBy} onCreatedByChange={e => setCreatedBy(e.target.value)}
                        type={type} onTypeChange={e => setType(e.target.value)}
                        year={year} onYearChange={e => setYear(e.target.value)}
                        questionsForQuiz={questionsForQuiz} onRemoveQuestion={handleRemoveQuestion}
                        onEditQuestion={handleEditQuestion}
                        quizSubjects={selectedQuizSubjects}
                        onQuizSubjectSelect={handleQuizSubjectSelect}
                        onRemoveQuizSubject={handleRemoveQuizSubject}
                        tags={tags}
                        moduleCount={moduleCount} onModuleCountChange={guardedSetModuleCount}
                        durations={durations} onDurationChange={setDurations}
                        isSaving={isSaving} error={error} isSaveDisabled={isSaveDisabled}
                        onSaveClick={handleSave} onCancelClick={onCancel}
                        isModelTest={isModelTest}
                    />
                </div>

                {/* Right Column: Question Selection */}
                <div className="col-lg-6 mb-4">
                    <div className="card shadow-sm p-4 h-full">
                        <QuestionFilterBar onFilter={handleFilterQuestions} />
                        <QuestionSelectionGrid
                            filters={questionFilters}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            onQuestionAssign={handleQuestionAssign}
                            selectedQuizSubjects={selectedQuizSubjects}
                            onEditQuestion={handleEditQuestion}
                        />
                    </div>
                </div>
            </div>
            {/* Custom CSS for DatePicker styling and badge background */}
            <style>
                {`
                /* Ensure native datetime-local input matches Bootstrap form-control */
                input[type="datetime-local"].form-control {
                    padding: 0.375rem 0.75rem; /* Match Bootstrap form-control padding */
                    border: 1px solid #ced4da; /* Match Bootstrap form-control border color */
                    border-radius: 0.25rem; /* Match Bootstrap form-control border-radius */
                    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.075); /* Match Bootstrap form-control shadow */
                    font-size: 1rem; /* Default font size */
                    line-height: 1.5; /* Default line height */
                    height: calc(1.5em + 0.75rem + 2px); /* Standard Bootstrap input height */
                }
                input[type="datetime-local"].form-control:focus {
                    color: #212529;
                    background-color: #fff;
                    border-color: #86b7fe; /* Bootstrap primary focus color */
                    outline: 0;
                    box-shadow: 0 0 0 0.25rem rgba(13,110,253,.25); /* Bootstrap primary focus shadow */
                }

                /* Custom badge style for quiz subjects */
                .badge.bg-primary-subtle {
                    background-color: #cce5ff !important; /* Lighter blue */
                    color: #004085 !important; /* Darker blue text */
                }
                `}
            </style>
            {editingQuestion && (
                <QuestionEditForm
                    question={editingQuestion}
                    onCancel={() => setEditingQuestion(null)}
                    onSave={() => setEditingQuestion(null)}
                />
            )}
        </div>
    );
}
